import * as accounting from 'accounting';
import * as I18n from 'i18n-js';
import { core_apimessages_Money as Money } from './rql';
import Currency from './currency';
import { CurrencyDescription } from './currency_descriptions';

export interface IMoneyConfig {
  decimalSeparator?: string;
  thousandsSeparator?: string;
  precision?: number;
  format?: string;
}

let config: IMoneyConfig = {};

export const USD_EN_CONFIG: IMoneyConfig = Object.freeze({
  decimalSeparator: '.',
  thousandsSeparator: ',',
  precision: 2,
  format: '%u%n',
});

export function getConfig(): IMoneyConfig {
  const defaultConfig: IMoneyConfig = {
    decimalSeparator: I18n.t('commons.currency.display.separator', { defaultValue: USD_EN_CONFIG.decimalSeparator }),
    thousandsSeparator: I18n.t('commons.currency.display.delimiter', { defaultValue: USD_EN_CONFIG.thousandsSeparator }),
    precision: I18n.t('commons.currency.display.precision', { defaultValue: USD_EN_CONFIG.precision }),
    format: I18n.t('commons.currency.display.format', { defaultValue: USD_EN_CONFIG.format }),
  };

  return {
    ...defaultConfig,
    ...config,
  };
}

export function hasPeriodDecimalSeparator() {
  return getConfig().decimalSeparator === '.';
}

export function setConfig(newConfig: IMoneyConfig) {
  config = newConfig;
}

export function resetConfig() {
  config = {};
}

export function formatNumber(value: number): string {
  const { decimalSeparator, thousandsSeparator, precision } = getConfig();

  return accounting.formatNumber(
    value,
    {
      precision,
      decimal: decimalSeparator,
      thousand: thousandsSeparator,
    },
  );
}

export function formatAmount(money: { amount: string; symbol: string }): string {
  const { decimalSeparator, thousandsSeparator, precision, format } = getConfig();

  return accounting.formatMoney(
    money.amount,
    {
      precision,
      format,
      decimal: decimalSeparator,
      thousand: thousandsSeparator,
      symbol: money.symbol,
    },
  );
}

export function formatAmountCents(money: { amountCents: number; currency: string }): string {
  return parseAmountCents(money.amountCents, money.currency).display;
}

export function parseAmount(
  inputAmount: string,
  currency: string,
  config?: IMoneyConfig,
): Required<Money> {
  config = config || getConfig();
  const separator = config.decimalSeparator;
  const parsedAmount = accounting.unformat(inputAmount, separator);
  const description = Currency.get(currency);
  const { symbol } = description;

  const amount = round(parsedAmount, description.precision).toString();

  return {
    currency,
    symbol,
    amount,
    amountCents: minorUnits(parsedAmount, description),
    display: formatAmount({ amount, symbol }),
  };
}

export function parseAmountCents(inputAmountCents: number, currency: string): Required<Money> {
  const { subunitToUnit, precision, symbol } = Currency.get(currency);

  const minorUnits = round(inputAmountCents, 0);
  const majorUnits = round(minorUnits / subunitToUnit, subunitToUnit);

  const amount = majorUnits.toFixed(precision);

  return {
    currency,
    symbol,
    amount,
    amountCents: minorUnits,
    display: formatAmount({ amount, symbol }),
  };
}

function round(value: number, precision: number) {
  return shift(Math.round(shift(value, +precision)), -precision);
}

function minorUnits(amount: number, currency: CurrencyDescription): number {
  return round(amount * currency.subunitToUnit, 0);
}

function shift(value: number, precision: number) {
  const numArray = (`${value}`).split('e');
  return +(`${numArray[0]}e${(numArray[1] ? (+numArray[1] + precision) : precision)}`);
}



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/commons/src/money.ts